import type { ShaderProgram } from './ShaderProgram';

export interface VertexAttrib {
  name: string;
  size: number; // components per vertex (e.g. 3 for vec3)
}

/**
 * Manages a VAO with vertex/index buffers. Encapsulates the
 * WebGL state needed to draw a single piece of geometry.
 */
export class Mesh {
  readonly vao: WebGLVertexArrayObject;
  private _gl: WebGL2RenderingContext;
  private _vbo: WebGLBuffer;
  private _ibo: WebGLBuffer | null = null;
  private _vertexCount: number;
  private _indexCount = 0;

  constructor(
    gl: WebGL2RenderingContext,
    shader: ShaderProgram,
    vertices: Float32Array,
    attribs: VertexAttrib[],
    indices?: Uint16Array | Uint32Array,
  ) {
    this._gl = gl;

    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    this.vao = vao;

    const stride = attribs.reduce((s, a) => s + a.size, 0);
    this._vertexCount = vertices.length / stride;

    this._vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this._vbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    let offset = 0;
    const bytesPerFloat = 4;
    for (const attr of attribs) {
      const loc = shader.getAttrib(attr.name);
      if (loc >= 0) {
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, attr.size, gl.FLOAT, false, stride * bytesPerFloat, offset * bytesPerFloat);
      }
      offset += attr.size;
    }

    if (indices) {
      this._ibo = gl.createBuffer()!;
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this._ibo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
      this._indexCount = indices.length;
    }

    gl.bindVertexArray(null);
  }

  draw(mode: number = this._gl.TRIANGLES): void {
    const gl = this._gl;
    gl.bindVertexArray(this.vao);

    if (this._ibo) {
      gl.drawElements(mode, this._indexCount, gl.UNSIGNED_SHORT, 0);
    } else {
      gl.drawArrays(mode, 0, this._vertexCount);
    }

    gl.bindVertexArray(null);
  }

  dispose(): void {
    const gl = this._gl;
    gl.deleteVertexArray(this.vao);
    gl.deleteBuffer(this._vbo);
    if (this._ibo) gl.deleteBuffer(this._ibo);
  }
}
